import { useEffect, useState } from 'react';
import { fetchUrl } from '@utils/api';

/** Signed URLs (and parsed JSON) for the files a video project page shows. */

// Keys are S3 keys; a full or root-relative URL (the dev preview's local samples) is used as it is.
const signed = async key => {
	if (!key) return null;
	if (/^(https?:|blob:|\/)/.test(key)) return key;
	return (await fetchUrl(`filelink?key=${key}`))?.url || null;
};

/** A signed URL for an S3 key, or null while loading / when there is no key. */
export const useSignedUrl = key => {
	const [url, setUrl] = useState(null);
	useEffect(() => {
		setUrl(null);
		if (!key) return undefined;
		let cancelled = false;
		signed(key)
			.then(u => !cancelled && setUrl(u))
			.catch(err => console.warn(`Could not sign ${key}:`, err));
		return () => {
			cancelled = true;
		};
	}, [key]);
	return url;
};

/** Parsed JSON behind an S3 key. */
export const useJsonFile = key => {
	const [doc, setDoc] = useState(null);
	useEffect(() => {
		setDoc(null);
		if (!key) return undefined;
		let cancelled = false;
		signed(key)
			.then(u => (u ? fetch(u).then(r => r.json()) : null))
			.then(d => !cancelled && setDoc(d))
			.catch(err => console.warn(`Could not load ${key}:`, err));
		return () => {
			cancelled = true;
		};
	}, [key]);
	return doc;
};

/** The best browser-playable copy of the uploaded video: a compressed variant if there is one, else the raw file. */
export const sourceVideoKey = video => {
	const c = video?.compressed_paths;
	if (c && typeof c === 'object') {
		const first = Object.values(c).find(v => typeof v === 'string');
		if (first) return first;
	}
	return video?.s3_raw_path || null;
};
